import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Globe, X, Loader2, Check, Search, ExternalLink } from "lucide-react";
import { api } from "../lib/api";

const BATCH = 8;

function ConfidenceBadge({ level }) {
  const map = {
    alta: { label: "Alta", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
    media: { label: "Media", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  };
  const cfg = map[level] || { label: "Nessun risultato", cls: "bg-slate-100 text-slate-500 border-slate-300" };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide border ${cfg.cls}`} data-testid={`sup-web-confidence-${level || "none"}`}>
      {cfg.label}
    </span>
  );
}

export default function SupplierWebsiteFinder({ onClose, onApplied, embedded = false }) {
  const [loadingList, setLoadingList] = useState(true);
  const [missing, setMissing] = useState([]);
  const [searching, setSearching] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [rows, setRows] = useState([]);
  const [started, setStarted] = useState(false);

  const loadMissing = useCallback(async () => {
    setLoadingList(true);
    try {
      const { data } = await api.get("/admin/suppliers/missing-websites");
      setMissing(data.suppliers || []);
    } catch (e) {
      toast.error("Impossibile caricare l'elenco fornitori");
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { loadMissing(); }, [loadMissing]);

  const runSearch = useCallback(async () => {
    const ids = missing.map((m) => m.id);
    if (ids.length === 0) return;
    setStarted(true);
    setSearching(true);
    setRows([]);
    setProgress({ done: 0, total: ids.length });
    let acc = [];
    for (let i = 0; i < ids.length; i += BATCH) {
      const batch = ids.slice(i, i + BATCH);
      try {
        const { data } = await api.post("/admin/suppliers/website-suggestions", { ids: batch });
        acc = acc.concat((data.suggestions || []).map((s) => ({ ...s, website: s.suggested_website || "", saving: false })));
        setRows([...acc]);
      } catch (e) {
        toast.error("Errore durante la ricerca di un gruppo");
      }
      setProgress({ done: Math.min(i + BATCH, ids.length), total: ids.length });
    }
    setSearching(false);
  }, [missing]);

  const updateWebsite = (id, website) => {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, website } : r)));
  };

  const saveRow = async (row) => {
    const url = (row.website || "").trim();
    if (!url) { toast.error("Inserisci un indirizzo prima di salvare"); return; }
    setRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, saving: true } : r)));
    try {
      await api.post(`/admin/suppliers/${row.id}/set-website`, { website: url });
      toast.success(`Sito salvato per ${row.name}`);
      setRows((rs) => rs.filter((r) => r.id !== row.id));
      setMissing((ms) => ms.filter((m) => m.id !== row.id));
      onApplied && onApplied();
    } catch (e) {
      toast.error("Salvataggio fallito");
      setRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, saving: false } : r)));
    }
  };

  const ignoreRow = (id) => setRows((rs) => rs.filter((r) => r.id !== id));

  const saveAll = async () => {
    const toSave = rows.filter((r) => (r.website || "").trim());
    if (toSave.length === 0) { toast.error("Nessun sito da salvare"); return; }
    for (const row of toSave) {
      // eslint-disable-next-line no-await-in-loop
      await saveRow(row);
    }
  };

  const header = (
    <div className="flex items-center justify-between px-6 py-4 border-b border-ink/10">
      <div className="flex items-center gap-2">
        <Globe size={18} className="text-ink" />
        <div className="font-display text-xl text-ink">Trova siti web mancanti</div>
      </div>
      {!embedded && (
        <button onClick={onClose} className="text-slate1 hover:text-ink" data-testid="sup-web-finder-close" aria-label="Chiudi">
          <X size={20} />
        </button>
      )}
    </div>
  );

  const body = (
    <div className="p-6 space-y-4">
      {loadingList ? (
        <div className="flex items-center gap-2 text-slate1 text-sm"><Loader2 size={16} className="animate-spin" /> Carico i fornitori…</div>
      ) : missing.length === 0 ? (
        <div className="text-sm text-slate1" data-testid="sup-web-finder-empty">
          Tutti i fornitori hanno già un sito web compilato. 🎉
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm text-slate1">
              <span className="font-semibold text-ink" data-testid="sup-web-finder-count">{missing.length}</span> fornitori senza sito web.
              {" "}I suggerimenti vengono cercati online: controlla e conferma tu ogni salvataggio.
            </div>
            {!started && (
              <button
                onClick={runSearch}
                disabled={searching}
                className="inline-flex items-center gap-2 px-4 py-2 bg-ink text-cream rounded-sm text-[11px] tracking-[0.2em] uppercase hover:bg-ink/90 disabled:opacity-50"
                data-testid="sup-web-finder-start"
              >
                <Search size={14} /> Cerca i siti online
              </button>
            )}
          </div>

          {searching && (
            <div className="flex items-center gap-2 text-sm text-slate1" data-testid="sup-web-finder-progress">
              <Loader2 size={16} className="animate-spin" />
              Ricerca in corso… {progress.done}/{progress.total}
            </div>
          )}

          {rows.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-[11px] tracking-[0.2em] uppercase text-slate1">Suggerimenti ({rows.length})</div>
                <button
                  onClick={saveAll}
                  className="inline-flex items-center gap-2 px-3 py-1.5 border border-ink/20 rounded-sm text-[11px] tracking-[0.15em] uppercase text-slate1 hover:bg-ink hover:text-cream hover:border-ink"
                  data-testid="sup-web-finder-save-all"
                >
                  <Check size={13} /> Salva tutti
                </button>
              </div>
              <div className="border border-ink/10 rounded-sm divide-y divide-ink/10">
                {rows.map((r) => (
                  <div key={r.id} className="p-3 flex flex-col md:flex-row md:items-center gap-3" data-testid={`sup-web-row-${r.id}`}>
                    <div className="md:w-48 shrink-0">
                      <div className="font-medium text-ink text-sm truncate" title={r.name}>{r.name}</div>
                      <div className="mt-1"><ConfidenceBadge level={r.confidence} /></div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <input
                          value={r.website}
                          onChange={(e) => updateWebsite(r.id, e.target.value)}
                          placeholder="https://…"
                          className="flex-1 min-w-0 border border-ink/20 px-3 py-2 rounded-sm bg-cream text-sm"
                          data-testid={`sup-web-input-${r.id}`}
                        />
                        {r.website ? (
                          <a href={r.website} target="_blank" rel="noreferrer" className="text-slate1 hover:text-ink shrink-0" title="Apri" data-testid={`sup-web-open-${r.id}`}>
                            <ExternalLink size={16} />
                          </a>
                        ) : null}
                      </div>
                      {r.candidates && r.candidates.length > 1 && (
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {r.candidates.map((c, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => updateWebsite(r.id, c.url)}
                              className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${r.website === c.url ? "bg-ink text-cream border-ink" : "border-ink/20 text-slate1 hover:border-ink"}`}
                              title={c.title}
                              data-testid={`sup-web-cand-${r.id}-${idx}`}
                            >
                              {c.url.replace(/^https?:\/\//, "")}
                            </button>
                          ))}
                        </div>
                      )}
                      {!r.suggested_website && (
                        <div className="mt-1 text-[11px] text-rose-700" data-testid={`sup-web-noresult-${r.id}`}>Nessun sito trovato automaticamente — inseriscilo a mano.</div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => saveRow(r)}
                        disabled={r.saving}
                        className="inline-flex items-center gap-1.5 px-3 py-2 bg-ink text-cream rounded-sm text-[11px] tracking-[0.15em] uppercase hover:bg-ink/90 disabled:opacity-50"
                        data-testid={`sup-web-save-${r.id}`}
                      >
                        {r.saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Salva
                      </button>
                      <button
                        onClick={() => ignoreRow(r.id)}
                        className="inline-flex items-center px-3 py-2 border border-ink/20 rounded-sm text-[11px] tracking-[0.15em] uppercase text-slate1 hover:bg-ink hover:text-cream hover:border-ink"
                        data-testid={`sup-web-ignore-${r.id}`}
                      >
                        Ignora
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  if (embedded) {
    return (
      <div className="bg-white rounded-sm border border-ink/10 shadow-sm" data-testid="sup-web-finder-page">
        {header}
        {body}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] bg-ink/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="sup-web-finder-modal">
      <div className="bg-white w-full max-w-4xl my-8 rounded-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {header}
        {body}
      </div>
    </div>
  );
}
